import React from 'react';
import { MapPin, Phone, Smartphone, Globe, Instagram, Twitter, Facebook } from 'lucide-react';
import { translate } from '../hooks/usePublicTranslation';

const RestaurantDetails = ({ settings, onClose, language = 'en' }) => {
  if (!settings) return null;

  const t = (key, params) => translate(key, language, params);

  const {
    address,
    website,
    telephone,
    gsm_number,
    whatsapp_enabled,
    location_url,
    instagram_url,
    twitter_url,
    facebook_url,
  } = settings;

  // Check if we have any contact info to display
  const hasContactInfo = address || website || telephone || gsm_number || location_url;
  const hasSocialMedia = instagram_url || twitter_url || facebook_url;

  if (!hasContactInfo && !hasSocialMedia) {
    return null; // Don't render if no info available
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="details-backdrop"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="details-modal">
        <div className="details-header">
          <h3>{t('restaurantDetails.title')}</h3>
          <button 
            className="details-close-btn"
            onClick={onClose}
            aria-label={t('publicMenu.close')}
          >
            ×
          </button>
        </div>

        <div className="details-content">
          {/* Contact Information */}
          {hasContactInfo && (
            <div className="details-section">
              <h4 className="details-section-title">{t('restaurantDetails.contact')}</h4>
              
              {address && (
                <div className="details-item">
                  <MapPin className="details-icon" size={20} />
                  <span>{address}</span>
                </div>
              )}

              {location_url && (
                <div className="details-item">
                  <a 
                    href={location_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="details-link"
                  >
                    <MapPin className="details-icon" size={20} />
                    <span>{t('restaurantDetails.viewOnMap')}</span>
                  </a>
                </div>
              )}

              {telephone && (
                <div className="details-item">
                  <a href={`tel:${telephone}`} className="details-link">
                    <Phone className="details-icon" size={20} />
                    <span>{telephone}</span>
                  </a>
                </div>
              )}

              {gsm_number && (
                <div className="details-item">
                  <a href={`tel:${gsm_number}`} className="details-link">
                    <Smartphone className="details-icon" size={20} />
                    <span>{gsm_number}</span>
                  </a>
                </div>
              )}

              {gsm_number && whatsapp_enabled && (
                <div className="details-item">
                  <a 
                    href={`https://wa.me/${gsm_number.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="details-link whatsapp-link"
                  >
                    <svg className="details-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    <span>{t('restaurantDetails.whatsapp')}</span>
                  </a>
                </div>
              )}

              {website && (
                <div className="details-item">
                  <a 
                    href={website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="details-link"
                  >
                    <Globe className="details-icon" size={20} />
                    <span>{website.replace(/^https?:\/\/(www\.)?/, '')}</span>
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Social Media */}
          {hasSocialMedia && (
            <div className="details-section">
              <h4 className="details-section-title">{t('restaurantDetails.followUs')}</h4>
              
              <div className="social-links">
                {instagram_url && (
                  <a 
                    href={instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link instagram"
                    aria-label="Instagram"
                  >
                    <Instagram size={24} />
                  </a>
                )}

                {twitter_url && (
                  <a 
                    href={twitter_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link twitter"
                    aria-label="Twitter"
                  >
                    <Twitter size={24} />
                  </a>
                )}

                {facebook_url && (
                  <a 
                    href={facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link facebook"
                    aria-label="Facebook"
                  >
                    <Facebook size={24} />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        <style>{`
          .details-backdrop {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 998;
            animation: fadeIn 0.25s ease-out;
            backdrop-filter: blur(4px);
            -webkit-backdrop-filter: blur(4px);
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          .details-modal {
            position: fixed;
            background: white;
            max-width: 500px;
            width: 90%;
            max-height: 80vh;
            overflow: auto;
            z-index: 999;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
            
            /* Desktop: centered modal */
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            border-radius: 16px;
            animation: scaleIn 0.25s ease-out;
          }

          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translate(-50%, -50%) scale(1);
            }
          }

          /* Mobile: Bottom sheet style */
          @media (max-width: 640px) {
            .details-modal {
              top: auto;
              bottom: 0;
              left: 0;
              right: 0;
              width: 100%;
              max-width: 100%;
              max-height: 85vh;
              transform: none;
              border-radius: 20px 20px 0 0;
              animation: slideFromBottom 0.3s cubic-bezier(0.32, 0.72, 0, 1);
            }

            @keyframes slideFromBottom {
              from {
                opacity: 0;
                transform: translateY(100%);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          }

          .details-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 24px;
            border-bottom: 1px solid #e5e7eb;
            position: sticky;
            top: 0;
            background: white;
            z-index: 1;
          }

          /* Drag indicator for mobile bottom sheet */
          @media (max-width: 640px) {
            .details-header::before {
              content: '';
              position: absolute;
              top: 8px;
              left: 50%;
              transform: translateX(-50%);
              width: 36px;
              height: 4px;
              background: #d1d5db;
              border-radius: 2px;
            }
            
            .details-header {
              padding-top: 24px;
            }
          }

          .details-header h3 {
            margin: 0;
            font-size: 20px;
            font-weight: 600;
            color: #1f2937;
          }

          .details-close-btn {
            background: #f3f4f6;
            border: none;
            font-size: 24px;
            line-height: 1;
            color: #6b7280;
            cursor: pointer;
            padding: 0;
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            transition: background-color 0.2s, color 0.2s, transform 0.2s;
          }

          .details-close-btn:hover {
            background-color: #e5e7eb;
            color: #1f2937;
            transform: scale(1.05);
          }

          .details-close-btn:active {
            transform: scale(0.95);
          }

          .details-content {
            padding: 24px;
          }

          .details-section {
            margin-bottom: 24px;
          }

          .details-section:last-child {
            margin-bottom: 0;
          }

          .details-section-title {
            font-size: 16px;
            font-weight: 600;
            color: #374151;
            margin: 0 0 16px 0;
          }

          .details-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 0;
            color: #4b5563;
          }

          .details-icon {
            flex-shrink: 0;
            color: #667eea;
          }

          .details-link {
            display: flex;
            align-items: center;
            gap: 12px;
            color: #667eea;
            text-decoration: none;
            transition: color 0.2s;
          }

          .details-link:hover {
            color: #764ba2;
          }

          .whatsapp-link {
            color: #25d366;
          }

          .whatsapp-link:hover {
            color: #128c7e;
          }

          .social-links {
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
          }

          .social-link {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 48px;
            height: 48px;
            border-radius: 12px;
            color: white;
            text-decoration: none;
            transition: transform 0.2s, box-shadow 0.2s;
          }

          .social-link:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }

          .social-link.instagram {
            background: linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%);
          }

          .social-link.twitter {
            background: #1da1f2;
          }

          .social-link.facebook {
            background: #1877f2;
          }

          @media (max-width: 640px) {
            .details-content {
              padding: 20px;
              padding-bottom: calc(20px + env(safe-area-inset-bottom));
            }

            .details-header h3 {
              font-size: 18px;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default RestaurantDetails;
